import React, { useState, useEffect } from 'react';
import {
    Box,
    Stack,
    Typography,
    Radio,
    RadioGroup,
    FormControlLabel,
    Checkbox,
    Link,
    useMediaQuery,
    Paper,
    Alert,
    CircularProgress,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import InputField from '../../components/InputField/InputField';
import { useNavigate, useParams } from 'react-router-dom';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { ActionButton, CancelButton } from '../../components/Button';
import { userService, CreateUserRequest, UpdateUserRequest, User } from '../../services/userService';
import { fileService } from '../../services/fileService';
import { useResponsiveToast } from '../../components/Toast/Toast';

const roles = ['Gensize Admin', 'Gensize Data Worker', 'Gensize Data Approver'];

interface FormData {
    username: string;
    email: string;
    password: string;
    is_active: boolean;
    selectedRoles: string[];
    profile_picture: File | null;
}

const AddUserPage = () => {
    const navigate = useNavigate();
    const { userId } = useParams<{ userId: string }>();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { success: showSuccess, error: showError } = useResponsiveToast();

    const isEditMode = !!userId;

    // Form state
    const [formData, setFormData] = useState<FormData>({
        username: '',
        email: '',
        password: '',
        is_active: true,
        selectedRoles: [],
        profile_picture: null,
    });

    // UI state
    const [loading, setLoading] = useState(false);
    const [imagePreview, setImagePreview] = useState<string | null>(null);
    const [existingProfilePicture, setExistingProfilePicture] = useState<string | null>(null);
    const [isProfilePictureEdited, setIsProfilePictureEdited] = useState(false);
    const [fetchingUser, setFetchingUser] = useState(false);
    const [fetchingProfilePicture, setFetchingProfilePicture] = useState(false);

    // Load user data if in edit mode
    useEffect(() => {
        if (isEditMode && userId) {
            loadUserData();
        }
    }, [isEditMode, userId]);

    const loadUserData = async () => {
        if (!userId) return;
        
        try {
            setFetchingUser(true);
            const user = await userService.getUser(userId);
            
            setFormData({
                username: user.username,
                email: user.email,
                password: '', // Don't populate password in edit mode
                is_active: user.is_active,
                selectedRoles: userService.mapRolesToDisplay(user.roles),
                profile_picture: null,
            });

            // Unblock the page immediately after setting form data
            setFetchingUser(false);

            // Load existing profile picture in the background if available
            if (user.profile_picture) {
                setFetchingProfilePicture(true);
                try {
                    const fileResponse = await fileService.previewFile(user.profile_picture);
                    const blob = fileResponse.data;
                    const imageUrl = URL.createObjectURL(blob);
                    setExistingProfilePicture(imageUrl);
                    setImagePreview(imageUrl);
                } catch (error) {
                    console.error('Error loading profile picture:', error);
                    // Continue without profile picture if it fails to load
                } finally {
                    setFetchingProfilePicture(false);
                }
            }
        } catch (error) {
            console.error('Full error object:', error);
            showError('Failed to load user data');
            console.error('Error loading user:', error);
            setFetchingUser(false);
        }
    };

    // Handle input changes
    const handleInputChange = (field: keyof FormData, value: any) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // Handle role selection
    const handleRoleChange = (role: string, checked: boolean) => {
        setFormData(prev => ({
            ...prev,
            selectedRoles: checked 
                ? [...prev.selectedRoles, role]
                : prev.selectedRoles.filter(r => r !== role)
        }));
    };

    // Validate file type
    const isValidImageType = (file: File): boolean => {
        const allowedTypes = ['image/png', 'image/jpg', 'image/jpeg'];
        return allowedTypes.includes(file.type.toLowerCase());
    };

    // Handle file upload
    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0] || null;
        
        if (file) {
            // Validate file type
            if (!isValidImageType(file)) {
                showError('Please select a valid image file (PNG, JPG, or JPEG)');
                return;
            }

            // Validate file size (max 5MB)
            if (file.size > 5 * 1024 * 1024) {
                showError('File size must be less than 5MB');
                return;
            }

            // Create preview URL
            const previewUrl = URL.createObjectURL(file);
            setImagePreview(previewUrl);
            setIsProfilePictureEdited(true);
            
            setFormData(prev => ({
                ...prev,
                profile_picture: file
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                profile_picture: null
            }));
            setImagePreview(null);
            setIsProfilePictureEdited(false);
        }
    };

    // Handle profile picture removal
    const handleRemoveProfilePicture = () => {
        setFormData(prev => ({ ...prev, profile_picture: null }));
        setImagePreview(null);
        setExistingProfilePicture(null);
        setIsProfilePictureEdited(true);
        
        // Clean up existing preview URL
        if (existingProfilePicture) {
            URL.revokeObjectURL(existingProfilePicture);
        }
    };

    // Clean up preview URL when component unmounts or image changes
    useEffect(() => {
        return () => {
            if (imagePreview && imagePreview !== existingProfilePicture) {
                URL.revokeObjectURL(imagePreview);
            }
            if (existingProfilePicture) {
                URL.revokeObjectURL(existingProfilePicture);
            }
        };
    }, [imagePreview, existingProfilePicture]);

    // Validate form
    const validateForm = (): boolean => {
        if (!formData.username.trim()) {
            showError('Username is required');
            return false;
        }
        if (!formData.email.trim()) {
            showError('Email is required');
            return false;
        }
        if (!formData.email.includes('@')) {
            showError('Please enter a valid email address');
            return false;
        }
        if (!isEditMode && !formData.password.trim()) {
            showError('Password is required');
            return false;
        }
        if (!isEditMode && formData.password.length < 6) {
            showError('Password must be at least 6 characters long');
            return false;
        }
        if (formData.selectedRoles.length === 0) {
            showError('Please select at least one role');
            return false;
        }
        return true;
    };

    // Handle form submission
    const handleSubmit = async () => {
        if (!validateForm()) return;

        try {
            setLoading(true);

            if (isEditMode && userId) {
                // Edit mode
                const updateUserData: UpdateUserRequest = {
                    username: formData.username.trim(),
                    email: formData.email.trim(),
                    is_active: formData.is_active,
                    roles: userService.mapDisplayRolesToApi(formData.selectedRoles),
                };

                // Only include password if it's been changed
                if (formData.password.trim()) {
                    updateUserData.password = formData.password;
                }

                // Update user data
                await userService.updateUser(userId, updateUserData);

                // Upload new profile picture if edited
                if (isProfilePictureEdited && formData.profile_picture) {
                    await userService.uploadProfilePicture(userId, formData.profile_picture);
                }

                showSuccess('User updated successfully!');
            } else {
                // Create mode
                const createUserData: CreateUserRequest = {
                    username: formData.username.trim(),
                    email: formData.email.trim(),
                    password: formData.password,
                    is_active: formData.is_active,
                    roles: userService.mapDisplayRolesToApi(formData.selectedRoles),
                    profile_picture: formData.profile_picture,
                };

                await userService.createUser(createUserData);
                showSuccess('User added successfully!');
            }

            setTimeout(() => {
                navigate('/users');
            }, 2000);

        } catch (err) {
            showError(err instanceof Error ? err.message : `Failed to ${isEditMode ? 'update' : 'create'} user`);
            console.error(`Error ${isEditMode ? 'updating' : 'creating'} user:`, err);
        } finally {
            setLoading(false);
        }
    };

    if (fetchingUser) {
        return (
            <Box sx={{ 
                background: '#FEFBFF', 
                minHeight: '100vh', 
                p: { sm: 4 },
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
            }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ background: '#FEFBFF', minHeight: '100vh', p: { sm: 4 } }}>
            <Paper
                elevation={0}
                sx={{
                    borderRadius: "28px",
                    backgroundColor: '#fff',
                    p: { xs: 2, sm: 3 },
                }}
            >
                {/* Header */}
                <Typography
                    fontFamily='Poppins, sans-serif'
                    fontSize={24}
                    fontWeight={600}
                    color='#132F5F'
                    mb={3}
                >
                    {isEditMode ? 'Edit User' : 'Add New User'}
                </Typography>

                {/* Form Section */}
                <Stack spacing={3}>
                    <InputField
                        label='Username'
                        placeholder='Enter username'
                        required
                        value={formData.username}
                        onChange={(e) => handleInputChange('username', e.target.value)}
                        sx={{
                            input: { fontWeight: 500 },
                            width: { xs: '100%', sm: '450px' },
                            height: '56px',
                        }}
                    />

                    <InputField
                        label='Email address'
                        type='email'
                        placeholder='Enter email address'
                        required
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        sx={{
                            width: { xs: '100%', sm: '450px' },
                            height: '56px',
                        }}
                    />

                    <InputField
                        label='Password'
                        type='password'
                        placeholder={isEditMode ? 'Leave blank to keep current password' : 'Enter password'}
                        required={!isEditMode}
                        value={formData.password}
                        onChange={(e) => handleInputChange('password', e.target.value)}
                        sx={{
                            width: { xs: '100%', sm: '450px' },
                            height: '56px',
                        }}
                    />

                    {/* Status Radio */}
                    <Box>
                        <Typography
                            fontSize={12}
                            fontFamily='Poppins, sans-serif'
                            fontWeight={400}
                            mb={1}
                        >
                            Status
                        </Typography>
                        <RadioGroup 
                            row 
                            value={formData.is_active ? 'Active' : 'Inactive'}
                            onChange={(e) => handleInputChange('is_active', e.target.value === 'Active')}
                        >
                            <FormControlLabel
                                value='Active'
                                control={
                                    <Radio
                                        sx={{
                                            color: '#C5C5C5',
                                            '&.Mui-checked': {
                                                color: '#132F5F',
                                            },
                                        }}
                                    />
                                }
                                label='Active'
                                sx={{ mr: 4 }}
                            />
                            <FormControlLabel
                                value='Inactive'
                                control={
                                    <Radio
                                        sx={{
                                            color: '#C5C5C5',
                                            '&.Mui-checked': {
                                                color: '#132F5F',
                                            },
                                        }}
                                    />
                                }
                                label='Inactive'
                            />
                        </RadioGroup>
                    </Box>

                    {/* Role Checkboxes */}
                    <Box>
                        <Typography
                            fontSize={12}
                            fontFamily='Poppins, sans-serif'
                            fontWeight={400}
                            mb={1}
                        >
                            Assignable roles *
                        </Typography>
                        <Stack spacing={1}>
                            {roles.map((role, i) => (
                                <FormControlLabel
                                    key={i}
                                    control={
                                        <Checkbox
                                            checked={formData.selectedRoles.includes(role)}
                                            onChange={(e) => handleRoleChange(role, e.target.checked)}
                                            sx={{
                                                color: '#C5C5C5',
                                                '&.Mui-checked': {
                                                    color: '#132F5F',
                                                },
                                            }}
                                        />
                                    }
                                    label={
                                        <Typography fontSize={14} fontFamily='Poppins, sans-serif'>
                                            {role}
                                        </Typography>
                                    }
                                />
                            ))}
                        </Stack>
                    </Box>

                    {/* File Upload Section */}
                    <Box
                        sx={{
                            border: '2px dashed #132F5F',
                            borderRadius: '16px',
                            padding: 3,
                            textAlign: 'center',
                            backgroundColor: '#FEFBFF',
                            width: isMobile ? 'auto' : 400,
                            mx: 'auto',
                            position: 'relative',
                            minHeight: imagePreview ? '200px' : 'auto',
                        }}
                    >
                        {fetchingProfilePicture ? (
                            // Show loader while fetching profile picture
                            <Stack spacing={2} alignItems='center' justifyContent='center' minHeight='200px'>
                                <CircularProgress size={40} sx={{ color: '#132F5F' }} />
                                <Typography
                                    fontFamily='Poppins, sans-serif'
                                    fontSize={14}
                                    color='#132F5F'
                                >
                                    Loading profile picture...
                                </Typography>
                            </Stack>
                        ) : imagePreview ? (
                            // Show image preview
                            <Box sx={{ position: 'relative', width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                <img
                                    src={imagePreview}
                                    alt="Profile preview"
                                    style={{
                                        maxWidth: '100%',
                                        maxHeight: '300px',
                                        width: 'auto',
                                        height: 'auto',
                                        objectFit: 'contain',
                                        borderRadius: '12px',
                                    }}
                                />
                                <CancelButton
                                    onClick={handleRemoveProfilePicture}
                                    sx={{
                                        position: 'absolute',
                                        top: 8,
                                        right: 8,
                                        minWidth: 'auto',
                                        width: 32,
                                        height: 32,
                                        borderRadius: '50%',
                                        backgroundColor: '#E53935',
                                        color: 'white',
                                        border: 'none',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                        '&:hover': {
                                            backgroundColor: '#C62828',
                                            border: 'none',
                                        },
                                    }}
                                >
                                    ✕
                                </CancelButton>
                            </Box>
                        ) : (
                            // Show upload interface when no image is selected
                            <Stack spacing={2} alignItems='center'>
                                <CloudUploadIcon sx={{ fontSize: 40, color: '#132F5F' }} />
                                
                                <Typography
                                    fontFamily='Poppins, sans-serif'
                                    fontWeight={500}
                                    fontSize={16}
                                    color='#132F5F'
                                >
                                    Upload Profile Picture
                                </Typography>
                                
                                {formData.profile_picture && (
                                    <Typography
                                        fontFamily='Poppins, sans-serif'
                                        fontSize={12}
                                        color='#666'
                                    >
                                        Selected: {formData.profile_picture.name}
                                    </Typography>
                                )}

                                {/* Hidden file input */}
                                <ActionButton
                                    component="label"
                                    sx={{
                                        px: 3,
                                        backgroundColor: '#132F5F',
                                        fontFamily: 'Poppins, sans-serif',
                                        fontWeight: 500,
                                        fontSize: 14,
                                        '&:hover': {
                                            backgroundColor: '#132F5F',
                                        },
                                    }}
                                >
                                    Choose from device
                                    <input 
                                        hidden 
                                        accept="image/png,image/jpg,image/jpeg" 
                                        type="file" 
                                        onChange={handleFileChange}
                                    />
                                </ActionButton>

                                {/* Camera Capture (Mobile only) */}
                                {isMobile && (
                                    <CancelButton
                                        component="label"
                                        startIcon={<PhotoCameraIcon />}
                                        sx={{
                                            px: 2,
                                            fontFamily: 'Poppins, sans-serif',
                                            fontWeight: 500,
                                            fontSize: 14,
                                            borderColor: '#132F5F',
                                            color: '#132F5F',
                                            width: "170px",
                                            '&:hover': {
                                                borderColor: '#132F5F',
                                                backgroundColor: 'transparent',
                                            },
                                        }}
                                    >
                                        Take Photo
                                        <input 
                                            hidden 
                                            type="file" 
                                            accept="image/png,image/jpg,image/jpeg" 
                                            capture="environment"
                                            onChange={handleFileChange}
                                        />
                                    </CancelButton>
                                )}

                                <Typography
                                    fontFamily='Poppins, sans-serif'
                                    fontSize={10}
                                    color='#666'
                                    sx={{ mt: 1 }}
                                >
                                    Accepted formats: PNG, JPG, JPEG (Max 5MB)
                                </Typography>
                            </Stack>
                        )}
                    </Box>

                    {/* Button group: Save & Cancel */}
                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={2}
                        justifyContent={isMobile ? 'center' : 'flex-start'}
                        alignItems='center'
                        mt={2}
                        width="100%"
                    >
                        <ActionButton
                            onClick={handleSubmit}
                            disabled={loading}
                            sx={{
                                backgroundColor: '#001F5B',
                                px: 4,
                                py: 1.5,
                                fontFamily: 'Poppins, sans-serif',
                                fontWeight: 500,
                                fontSize: 14,
                                width: { xs: '100%', sm: 'auto' },
                                '&:hover': {
                                    backgroundColor: '#001742',
                                },
                                '&:disabled': {
                                    backgroundColor: '#ccc',
                                },
                            }}
                        >
                            {loading ? (
                                <CircularProgress size={20} color="inherit" />
                            ) : (
                                `✓ ${isEditMode ? 'Update' : 'Save'}`
                            )}
                        </ActionButton>
                        <CancelButton
                            onClick={() => navigate('/users')}
                            disabled={loading}
                            sx={{
                                borderColor: '#E53935',
                                color: '#E53935',
                                borderRadius: '12px',
                                px: 4,
                                py: 1.5,
                                fontFamily: 'Poppins, sans-serif',
                                fontWeight: 500,
                                fontSize: 14,
                                width: { xs: '100%', sm: 'auto' },
                                '&:hover': {
                                    borderColor: '#E53935',
                                    backgroundColor: 'transparent',
                                },
                                '&:disabled': {
                                    borderColor: '#ccc',
                                    color: '#ccc',
                                },
                            }}
                        >
                            ✕ Cancel
                        </CancelButton>
                    </Stack>
                </Stack>
            </Paper>
        </Box>
    );
};

export default AddUserPage;
